import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ----------------------------------------------------------------------------------
// Types for Cohort Analytics (Week 6)
// ----------------------------------------------------------------------------------

export type CohortGrouping = 'daily' | 'weekly' | 'monthly';

export interface CohortPeriodData {
  period: number;
  retainedCount: number;
  retentionRate: number;
  revenue: number;
  avgRevenuePerPatron: number;
  churnedCount: number;
  churnRate: number;
}

export interface CohortData {
  cohortDate: string; // ISO date
  cohortSize: number;
  periods: CohortPeriodData[];
}

export interface CohortAnalysisResponse {
  cohorts: CohortData[];
  overallStats: {
    avgRetentionMonth1: number;
    avgRetentionMonth3: number;
    avgRetentionMonth6: number;
    avgLifetimeValue: number;
    avgChurnRate: number;
    bestPerformingCohort: {
      date: string;
      retentionRate: number;
    };
    worstPerformingCohort: {
      date: string;
      retentionRate: number;
    };
  };
  heatmapData: number[][];
}

export interface CohortSummary {
  avgRetentionMonth1: number;
  avgRetentionMonth3: number;
  avgRetentionMonth6: number;
  avgLifetimeValue: number;
  avgChurnRate: number;
  totalCohorts: number;
  bestPerformingCohort: {
    date: string;
    retentionRate: number;
  };
  worstPerformingCohort: {
    date: string;
    retentionRate: number;
  };
}

export interface RetentionCurveResponse {
  grouping: CohortGrouping;
  curve: {
    period: number;
    avgRetentionRate: number;
    cohortCount: number;
  }[];
}

export interface TierCohortComparison {
  tierId: string;
  tierName: string;
  avgRetention: number;
  cohortCount: number;
  totalPatrons: number;
  retentionByPeriod: { period: number; retentionRate: number }[];
}

export interface TierComparisonResponse {
  grouping: CohortGrouping;
  tiers: TierCohortComparison[];
}

export interface HeatmapData {
  grouping: CohortGrouping;
  cohortLabels: string[];
  periodLabels: number[];
  heatmapData: number[][];
}

// ----------------------------------------------------------------------------------
// Service Client
// ----------------------------------------------------------------------------------
class CohortAnalyticsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  /**
   * Get full cohort analysis with retention data
   */
  async getCohortAnalysis(params: {
    grouping?: CohortGrouping;
    startDate?: string;
    endDate?: string;
    maxPeriods?: number;
    tierIds?: string[];
  } = {}): Promise<CohortAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.grouping) queryParams.append('grouping', params.grouping);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.maxPeriods) queryParams.append('maxPeriods', params.maxPeriods.toString());
    if (params.tierIds && params.tierIds.length > 0) {
      queryParams.append('tierIds', params.tierIds.join(','));
    }

    const url = `${API_BASE_URL}/api/analytics/cohort/analysis?${queryParams.toString()}`;
    const response = await axios.get(url, this.getAuthHeaders());

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load cohort analysis');
    }

    return response.data.data;
  }

  /**
   * Get average retention curve across all cohorts
   */
  async getRetentionCurve(params: {
    grouping?: CohortGrouping;
    maxPeriods?: number;
  } = {}): Promise<RetentionCurveResponse> {
    const queryParams = new URLSearchParams();
    if (params.grouping) queryParams.append('grouping', params.grouping);
    if (params.maxPeriods) queryParams.append('maxPeriods', params.maxPeriods.toString());

    const url = `${API_BASE_URL}/api/analytics/cohort/retention-curve?${queryParams.toString()}`;
    const response = await axios.get(url, this.getAuthHeaders());

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load retention curve');
    }

    return response.data.data;
  }

  /**
   * Compare cohort performance across tiers
   */
  async getTierComparison(params: {
    tierIds: string[];
    grouping?: CohortGrouping;
  }): Promise<TierComparisonResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('tierIds', params.tierIds.join(','));
    if (params.grouping) queryParams.append('grouping', params.grouping);

    const url = `${API_BASE_URL}/api/analytics/cohort/tier-comparison?${queryParams.toString()}`;
    const response = await axios.get(url, this.getAuthHeaders());

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load tier comparison');
    }

    return response.data.data;
  }

  /**
   * Get cohort summary for dashboard
   */
  async getCohortSummary(): Promise<CohortSummary> {
    const url = `${API_BASE_URL}/api/analytics/cohort/summary`;
    const response = await axios.get(url, this.getAuthHeaders());

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load cohort summary');
    }

    return response.data.data;
  }

  /**
   * Get heatmap data for cohort visualization
   */
  async getHeatmapData(params: {
    grouping?: CohortGrouping;
    maxPeriods?: number;
  } = {}): Promise<HeatmapData> {
    const queryParams = new URLSearchParams();
    if (params.grouping) queryParams.append('grouping', params.grouping);
    if (params.maxPeriods) queryParams.append('maxPeriods', params.maxPeriods.toString());

    const url = `${API_BASE_URL}/api/analytics/cohort/heatmap?${queryParams.toString()}`;
    const response = await axios.get(url, this.getAuthHeaders());

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to load heatmap data');
    }

    return response.data.data;
  }
}

// ----------------------------------------------------------------------------------
// Helper utilities
// ----------------------------------------------------------------------------------
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

export const formatCohortDate = (dateString: string, grouping: CohortGrouping): string => {
  const date = new Date(dateString);
  switch (grouping) {
    case 'daily':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'weekly':
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    case 'monthly':
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  }
};

export const getRetentionColor = (rate: number): string => {
  if (rate >= 80) return '#10b981'; // green-500
  if (rate >= 60) return '#3b82f6'; // blue-500
  if (rate >= 40) return '#f59e0b'; // amber-500
  if (rate >= 20) return '#ef4444'; // red-500
  return '#991b1b'; // red-900
};

// ----------------------------------------------------------------------------------
// Export singleton
// ----------------------------------------------------------------------------------
const cohortAnalyticsService = new CohortAnalyticsService();
export default cohortAnalyticsService;
